import React, { useMemo, useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../contexts/AuthContext'
import { useMenu } from '../../hooks/useMenu'
import { getIconForTela } from '../../utils/iconMapper'
import { TelaResponse } from '../../types/api'
import { NivelAcesso } from '../../types/auth'
import { isMobileDevice } from '../../utils/device'
import {
    LayoutDashboard,
    Users,
    Building2,
    History,
    FileText,
    Settings,
    Grid,
    FolderTree,
    Wrench,
    DatabaseBackup,
    ChevronDown,
    ChevronRight,
    AlertCircle,
    Star,
    StarOff
} from 'lucide-react'

interface SidebarProps {
    open: boolean
    setOpen: (open: boolean) => void
}

interface MenuItem {
    text: string
    icon?: React.ReactNode
    path?: string
    requiredLevel?: NivelAcesso
    children?: MenuItem[]
    abrirNovaAba?: boolean
    onlySuperAdminMode?: boolean
    requiresEmpresa?: boolean
}

const ModernSidebar: React.FC<SidebarProps> = ({ open, setOpen }) => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const location = useLocation()
    const { hasPermission, isSuperAdminMode, isAdminMode, getEmpresaId } = useAuth()
    const [favorites, setFavorites] = useState<string[]>([])

    // Removido useEffect que invalidava queries em loop/montagem desnecessária
    // O useMenu já está configurado com staleTime: 0 e refetchOnMount: true

    const empresaId = getEmpresaId()
    const { menuItems: dynamicMenuItems, isLoading: isLoadingMenu } = useMenu(
        'GALATEA_WEB',
        !isSuperAdminMode() ? empresaId : undefined
    )

    const { menuItems: adminMenuItems } = useMenu(
        'ADMIN',
        undefined
    )

    const [openSubmenus, setOpenSubmenus] = useState<Record<string, boolean>>({})

    const toggleSubmenu = (key: string) => {
        setOpenSubmenus((prev) => ({ ...prev, [key]: !prev[key] }))
    }

    useEffect(() => {
        try {
            const raw = localStorage.getItem('galatea_favorites_menu')
            if (raw) {
                const arr = JSON.parse(raw)
                if (Array.isArray(arr)) setFavorites(arr.filter((p) => typeof p === 'string'))
            }
        } catch {}
    }, [])

    const persistFavorites = (arr: string[]) => {
        setFavorites(arr)
        try {
            localStorage.setItem('galatea_favorites_menu', JSON.stringify(arr))
        } catch {}
    }

    const toggleFavorite = (path: string) => {
        if (!path) return
        if (favorites.includes(path)) {
            persistFavorites(favorites.filter((p) => p !== path))
        } else {
            persistFavorites([...favorites, path])
        }
    }

        const staticMenuItems: MenuItem[] = useMemo(() => {
        return [
            {
                text: 'Dashboard',
                icon: <LayoutDashboard size={20} />,
                path: '/dashboard',
                requiredLevel: NivelAcesso.ADMINISTRADOR,
            },
            {
                text: t('menu.groups'),
                icon: <Users size={20} />,
                path: '/grupos',
                requiredLevel: NivelAcesso.ADMINISTRADOR,
                onlySuperAdminMode: true,
            },
            {
                text: t('menu.companies'),
                icon: <Building2 size={20} />,
                path: '/empresas',
                requiredLevel: NivelAcesso.ADMINISTRADOR,
                onlySuperAdminMode: true,
            },
            {
                text: 'Parâmetros do Sistema',
                icon: <Settings size={20} />,
                path: '/parametros',
                requiredLevel: NivelAcesso.ADMINISTRADOR,
                onlySuperAdminMode: true,
            },
            {
                text: 'Documentação Thor',
                icon: <FileText size={20} />,
                path: '/documentacao/thor',
                requiredLevel: NivelAcesso.ADMINISTRADOR,
            },
            {
                text: 'Documentação Galatea Auth',
                icon: <FileText size={20} />,
                path: '/documentacao/auth',
                requiredLevel: NivelAcesso.ADMINISTRADOR,
            },
            {
                text: 'Áreas Funcionais',
                icon: <FolderTree size={20} />,
                path: '/areas-funcionais',
                requiredLevel: NivelAcesso.ADMINISTRADOR,
                onlySuperAdminMode: true,
            },
            {
                text: 'Manutenção de Usuários',
                icon: <Wrench size={20} />,
                path: '/usuarios/manutencao',
                requiredLevel: NivelAcesso.ADMINISTRADOR,
                onlySuperAdminMode: true,
            },
            {
                text: 'Audit Log',
                icon: <FileText size={20} />,
                path: '/audit-log',
                requiredLevel: NivelAcesso.ADMINISTRADOR,
                onlySuperAdminMode: true,
            },
            {
                text: 'Error log',
                icon: <AlertCircle size={20} />,
                path: '/log-erros',
                requiredLevel: NivelAcesso.ADMINISTRADOR,
                onlySuperAdminMode: true,
            },
            {
                text: 'Backups',
                icon: <DatabaseBackup size={20} />,
                path: '/backups',
                requiredLevel: NivelAcesso.ADMINISTRADOR,
                onlySuperAdminMode: true,
            },
            {
                text: 'Gerenciar Telas (Global)',
                icon: <Grid size={20} />,
                path: '/telas',
                requiredLevel: NivelAcesso.ADMINISTRADOR,
                onlySuperAdminMode: true,
            },
            {
                text: 'Menu ThorJava',
                icon: <Grid size={20} />,
                path: '/thorjava/menu',
                requiredLevel: NivelAcesso.ADMINISTRADOR,
                onlySuperAdminMode: true,
            },
            {
                text: t('menu.systemGalatea'),
                icon: <Grid size={20} />,
                path: '/sistema-galatea',
                requiredLevel: NivelAcesso.ADMINISTRADOR,
                onlySuperAdminMode: true,
            },
            {
                text: t('menu.sessions'),
                icon: <History size={20} />,
                path: '/sessoes',
                requiredLevel: NivelAcesso.ADMINISTRADOR,
                requiresEmpresa: false,
            },
            {
                text: t('menu.logs'),
                icon: <FileText size={20} />,
                path: '/logs',
                requiredLevel: NivelAcesso.ADMINISTRADOR,
                requiresEmpresa: false,
            },
        ]
    }, [t])

    const convertTelaToMenuItem = (tela: TelaResponse & { children?: TelaResponse[] }): MenuItem | null => {
        if (!tela.ativo || tela.deleted_at) return null

        const rotasExcluidas = ['/dicionario-dados', '/config/dicionario-dados', '/dicionariothor', '/config/dicionariothor']
        if (rotasExcluidas.includes(tela.rota)) return null

        const nomesExcluidos = ['Dicionário de Dados', 'Dicionário Thor', 'Dicionario de Dados', 'Dicionario Thor']
        if (nomesExcluidos.includes(tela.nome)) return null

        const menuItem: MenuItem = {
            text: tela.nome,
            icon: getIconForTela(tela.nome), // Mantendo ícone MUI original por enquanto
            path: tela.rota,
            abrirNovaAba: !!tela.abrir_nova_aba,
        }

        // layout_tipo CARD: não expandir submenu — ao clicar, navega para a rota e exibe cards dos filhos na página
        const isCardLayout = tela.layout_tipo === 'CARD'
        if (tela.children && tela.children.length > 0 && !isCardLayout) {
            menuItem.children = tela.children
                .map((child) => convertTelaToMenuItem(child))
                .filter((item): item is MenuItem => item !== null)
        }

        return menuItem
    }

    const menuItems: MenuItem[] = useMemo(() => {
        const items: MenuItem[] = []
        const pathsSeen = new Set<string>()

        const addWithoutDuplicate = (item: MenuItem) => {
            const path = item.path || ''
            if (path && pathsSeen.has(path)) return
            if (path) pathsSeen.add(path)
            items.push(item)
        }

        const contextFilteredStatic = staticMenuItems.filter((item) => {
            if (item.onlySuperAdminMode && !(isSuperAdminMode() || isAdminMode())) return false
            if (item.requiresEmpresa && !empresaId) return false
            return true
        })

        contextFilteredStatic.forEach(addWithoutDuplicate)

        if ((isSuperAdminMode() || isAdminMode()) && adminMenuItems && adminMenuItems.length > 0) {
            const adminItems = adminMenuItems
                .map((tela) => convertTelaToMenuItem(tela))
                .filter((item): item is MenuItem => item !== null)
            adminItems.forEach(addWithoutDuplicate)
        }

        return items
    }, [staticMenuItems, dynamicMenuItems, adminMenuItems, isSuperAdminMode, isAdminMode, empresaId])

    const filteredMenuItems = useMemo(() => {
        const filterItems = (items: MenuItem[]): MenuItem[] =>
            items
                .map((item) => {
                    const hasAccess = !item.requiredLevel || hasPermission(item.requiredLevel)
                    if (item.children) {
                        const filteredChildren = filterItems(item.children)
                        if (hasAccess && filteredChildren.length > 0) {
                            return { ...item, children: filteredChildren }
                        }
                        return hasAccess ? { ...item, children: [] } : null
                    }
                    return hasAccess ? item : null
                })
                .filter((item): item is MenuItem => item !== null)
        return filterItems(menuItems)
    }, [menuItems, hasPermission])

    const allFinalItems = useMemo(() => {
        const out: MenuItem[] = []
        const walk = (items: MenuItem[]) => {
            items.forEach((it) => {
                if (it.children && it.children.length > 0) {
                    walk(it.children)
                } else if (it.path) {
                    out.push(it)
                }
            })
        }
        walk(filteredMenuItems)
        return out
    }, [filteredMenuItems])

    const favoriteItems = useMemo(() => {
        const map = new Map(allFinalItems.map((i) => [i.path!, i]))
        return favorites
            .map((p) => map.get(p))
            .filter((x): x is MenuItem => !!x)
    }, [favorites, allFinalItems])

    const renderMenuItems = (items: MenuItem[], depth = 0) => {
        return items.map((item, index) => {
            const key = `${item.path || item.text}-${depth}-${index}`
            const hasChildren = item.children && item.children.length > 0
            const isExpanded = openSubmenus[key] ?? false
            const isActive = item.path && location.pathname === item.path

            const handleClick = () => {
                if (hasChildren) {
                    toggleSubmenu(key)
                } else if (item.path) {
                    const url = `${window.location.origin}${item.path}`
                    if (item.abrirNovaAba && !isMobileDevice()) {
                        window.open(url, '_blank', 'noopener,noreferrer')
                    } else {
                        navigate(item.path)
                    }
                    setOpen(false)
                }
            }

            return (
                <div key={key} className="flex flex-col">
                    <button
                        onClick={handleClick}
                        className={`
              flex items-center w-full min-h-[2.75rem] px-4 py-3 text-sm font-medium transition-colors
              ${isActive ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' : 'text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'}
              ${depth > 0 ? 'pl-' + (4 + depth * 4) : ''}
            `}
                    >
                        {item.icon && <span className="mr-3 flex shrink-0 items-center">{item.icon}</span>}
                        {open && <span className="flex flex-1 items-center text-left truncate">{item.text}</span>}
                        {!hasChildren && open && item.path && (
                            <span
                                onClick={(e) => {
                                    e.stopPropagation()
                                    toggleFavorite(item.path!)
                                }}
                                className="ml-2 inline-flex rounded p-1 hover:bg-slate-100 dark:hover:bg-slate-800"
                                title={favorites.includes(item.path!) ? 'Remover dos Favoritos' : 'Adicionar aos Favoritos'}
                            >
                                {favorites.includes(item.path!) ? <Star size={16} className="text-amber-500" /> : <StarOff size={16} className="text-slate-400" />}
                            </span>
                        )}
                        {hasChildren && open && (
                            isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />
                        )}
                    </button>

                    {hasChildren && isExpanded && open && (
                        <div className="bg-slate-50 dark:bg-slate-800/50">
                            {renderMenuItems(item.children!, depth + 1)}
                        </div>
                    )}
                </div>
            )
        })
    }

    return (
        <aside
            className={`
        fixed left-0 top-0 z-40 h-screen transition-all duration-300 ease-in-out border-r border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900
        ${open ? 'w-64' : 'w-16'}
      `}
        >
            {/* Header do Sidebar - Logo Galateaia */}
            <div className={`flex h-[4.6rem] items-center border-b border-slate-200 dark:border-slate-700 shrink-0 ${open ? 'px-4' : 'justify-center'}`}>
                <div className="flex items-center gap-3 w-full">
                    {/* Logo: /logo-galatea.png (salvar na pasta public) */}
                    <img 
                        src="/logo-galatea.png" 
                        alt="Logo Galatea" 
                        className="h-8 w-auto object-contain"
                        onError={(e) => {
                            e.currentTarget.style.display = 'none';
                            e.currentTarget.nextElementSibling?.classList.remove('hidden');
                        }} 
                    />
                    {/* Fallback visual (G azul) */}
                    <div className="h-8 w-8 bg-blue-600 rounded flex items-center justify-center text-white font-bold text-lg hidden">G</div>
                    
                    {open && (
                        <span className="text-base font-semibold text-slate-700 dark:text-slate-200">Galateaia</span>
                    )}
                </div>
            </div>

            <div className="h-[calc(100vh-4.6rem)] scrollbar-standard">
                {isLoadingMenu ? (
                    <div className="p-4 text-center text-sm text-slate-500 dark:text-slate-400">{t('common.loading')}</div>
                ) : (
                    <nav className="mt-4 pb-4">
                        {open && favoriteItems.length > 0 && (
                            <div className="px-3 mb-4">
                                <div className="px-2 text-xs font-semibold tracking-wide text-slate-500 dark:text-slate-400 mb-2">Favoritos</div>
                                <div className="flex flex-col gap-1.5">
                                    {favoriteItems.map((fav) => (
                                        <button
                                            key={fav.path}
                                            onClick={() => {
                                                const url = `${window.location.origin}${fav.path}`
                                                if (!isMobileDevice()) {
                                                    window.open(url, '_blank', 'noopener,noreferrer')
                                                } else {
                                                    navigate(fav.path!)
                                                }
                                                setOpen(false)
                                            }}
                                            className="flex items-center gap-2 rounded-md px-3 py-2 text-sm bg-slate-50 hover:bg-slate-100 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200"
                                        >
                                            {fav.icon || <Star size={14} className="text-amber-500" />}
                                            <span className="truncate">{fav.text}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                        {renderMenuItems(filteredMenuItems)}
                    </nav>
                )}
            </div>
        </aside>
    )
}

export default ModernSidebar
